import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Tabs, Tab, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Checkbox, IconButton, Tooltip, Pagination, Alert, Select, MenuItem,
  InputLabel, FormControl
} from '@mui/material';
import {
  CheckCircle as ApproveIcon, Cancel as RejectIcon, Refresh as RefreshIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Upload as UploadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface SuggestionUser { _id: string; name?: string; email?: string; }
interface Suggestion {
  _id: string; vendorName: string; standardizedName: string; proposedAlias: string;
  source: string; suggestedBy: SuggestionUser[]; frequency: number; reportIds: string[];
  status: string; createdAt: string; updatedAt: string;
}
interface GenderRef { unit?: string; method?: string; low?: string; high?: string; referenceRange?: string; }
interface VendorMaster {
  _id: string; vendorName: string; standardizedName: string; aliases: string[];
  category?: string; isActive: boolean; vendorAliases?: string[];
  reference?: { male?: GenderRef; female?: GenderRef; neutral?: GenderRef };
}
interface VendorInfo { vendorName: string; vendorAliases: string[]; parameterCount: number; }

const sourceLabel = (s: string) =>
  s === 'tier4_cross_ref' ? 'Cross-ref' : s === 'tier5_ai' ? 'AI' : s === 'tier6_manual_pick' ? 'Manual' : s;

const VendorMasterManagement: React.FC = () => {
  const { canEditMasters } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  // The alias-suggestion queue (tab 0) is admin-only, so nurses open on the Vendor Master tab
  // (and fetchSuggestions never runs for them - it only runs when tab === 0).
  const [tab, setTab] = useState(canEditMasters ? 0 : 1);

  // ===== Vendor list =====
  const [vendors, setVendors] = useState<VendorInfo[]>([]);
  const [selectedVendor, setSelectedVendor] = useState('');

  // ===== Suggestions tab =====
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actingId, setActingId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; ids: string[]; reason: string }>({ open: false, ids: [], reason: '' });

  // ===== Vendor Master tab =====
  const [master, setMaster] = useState<VendorMaster[]>([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterPage, setMasterPage] = useState(1);
  const [masterPages, setMasterPages] = useState(1);

  // ===== Dialogs =====
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorAliases, setNewVendorAliases] = useState('');
  const [deleteVendorOpen, setDeleteVendorOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [addParamOpen, setAddParamOpen] = useState(false);
  const [paramForm, setParamForm] = useState({ standardizedName: '', aliases: '', category: '' });
  const [editParam, setEditParam] = useState<VendorMaster | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [csvVendorAliases, setCsvVendorAliases] = useState('');
  const [csvPreview, setCsvPreview] = useState<Array<{ standardizedName: string; aliases: string; category: string }>>([]);
  const [importing, setImporting] = useState(false);
  const [dialogBusy, setDialogBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // ===== Fetch vendors =====
  const fetchVendors = useCallback(async () => {
    try {
      const res = await api.get('/vendor-suggestions/master/vendors');
      setVendors(res.data.vendors || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  // ===== Suggestions =====
  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { status: 'pending', page, limit: 20 };
      if (selectedVendor) params.vendorName = selectedVendor;
      const res = await api.get('/vendor-suggestions', { params });
      setSuggestions(res.data.data || []);
      setPages(res.data.pagination?.pages || 1);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to load suggestions', { variant: 'error' });
    } finally { setLoading(false); }
  }, [page, selectedVendor, enqueueSnackbar]);

  // ===== Master =====
  const fetchMaster = useCallback(async () => {
    setMasterLoading(true);
    try {
      const params: any = { page: masterPage, limit: 50 };
      if (selectedVendor) params.vendorName = selectedVendor;
      const res = await api.get('/vendor-suggestions/master', { params });
      setMaster(res.data.data || []);
      setMasterPages(res.data.pagination?.pages || 1);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to load vendor master', { variant: 'error' });
    } finally { setMasterLoading(false); }
  }, [masterPage, selectedVendor, enqueueSnackbar]);

  useEffect(() => { if (tab === 0) fetchSuggestions(); }, [tab, fetchSuggestions]);
  useEffect(() => { if (tab === 1) fetchMaster(); }, [tab, fetchMaster]);

  // ===== Suggestion actions =====
  const handleApprove = async (id: string) => {
    setActingId(id);
    try {
      await api.put(`/vendor-suggestions/${id}/approve`);
      enqueueSnackbar('Approved — alias added to vendor master', { variant: 'success' });
      await fetchSuggestions();
    } catch (err: any) { enqueueSnackbar(err.response?.data?.message || 'Approve failed', { variant: 'error' }); }
    finally { setActingId(null); }
  };
  const openRejectDialog = (ids: string[]) => setRejectDialog({ open: true, ids, reason: '' });
  const submitReject = async () => {
    if (!rejectDialog.reason.trim()) { enqueueSnackbar('Reason required', { variant: 'warning' }); return; }
    setBulkBusy(true);
    try {
      if (rejectDialog.ids.length === 1) {
        await api.put(`/vendor-suggestions/${rejectDialog.ids[0]}/reject`, { reason: rejectDialog.reason });
      } else {
        await api.post('/vendor-suggestions/bulk-reject', { ids: rejectDialog.ids, reason: rejectDialog.reason });
      }
      enqueueSnackbar('Rejected', { variant: 'success' });
      setRejectDialog({ open: false, ids: [], reason: '' }); setSelected(new Set());
      await fetchSuggestions();
    } catch (err: any) { enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' }); }
    finally { setBulkBusy(false); }
  };
  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post('/vendor-suggestions/bulk-approve', { ids: Array.from(selected) });
      enqueueSnackbar(`Approved ${res.data.approved}`, { variant: 'success' });
      setSelected(new Set()); await fetchSuggestions();
    } catch (err: any) { enqueueSnackbar('Failed', { variant: 'error' }); }
    finally { setBulkBusy(false); }
  };
  const toggleSelected = (id: string) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); setSelected(n); };
  const toggleSelectAll = () => { if (selected.size === suggestions.length) setSelected(new Set()); else setSelected(new Set(suggestions.map(s => s._id))); };

  // ===== Vendor CRUD =====
  const handleDeleteVendor = async () => {
    if (!selectedVendor) return;
    setDeleteBusy(true);
    try {
      const res = await api.delete(`/vendor-suggestions/master/vendor/${encodeURIComponent(selectedVendor)}`);
      enqueueSnackbar(
        `Deleted "${selectedVendor}" — ${res.data.deletedRows} row(s)` +
        (res.data.deletedSuggestions ? `, ${res.data.deletedSuggestions} pending suggestion(s)` : ''),
        { variant: 'success' }
      );
      setDeleteVendorOpen(false);
      setSelectedVendor('');
      await fetchVendors();
      if (tab === 1) await fetchMaster();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleCreateVendor = async () => {
    if (!newVendorName.trim()) { enqueueSnackbar('Vendor name required', { variant: 'warning' }); return; }
    setDialogBusy(true);
    try {
      await api.post('/vendor-suggestions/master/vendor', {
        vendorName: newVendorName.trim(),
        vendorAliases: newVendorAliases.split(',').map(a => a.trim()).filter(Boolean)
      });
      enqueueSnackbar(`Vendor "${newVendorName}" created`, { variant: 'success' });
      setAddVendorOpen(false); setNewVendorName(''); setNewVendorAliases('');
      await fetchVendors();
    } catch (err: any) { enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' }); }
    finally { setDialogBusy(false); }
  };

  // ===== Parameter CRUD =====
  const handleCreateParameter = async () => {
    if (!selectedVendor) { enqueueSnackbar('Select a vendor first', { variant: 'warning' }); return; }
    if (!paramForm.standardizedName.trim()) { enqueueSnackbar('Standardized name required', { variant: 'warning' }); return; }
    setDialogBusy(true);
    try {
      await api.post('/vendor-suggestions/master/parameter', {
        vendorName: selectedVendor,
        standardizedName: paramForm.standardizedName.trim(),
        aliases: paramForm.aliases.split(',').map(a => a.trim()).filter(Boolean),
        category: paramForm.category.trim() || null
      });
      enqueueSnackbar('Parameter added', { variant: 'success' });
      setAddParamOpen(false); setParamForm({ standardizedName: '', aliases: '', category: '' });
      await fetchMaster(); await fetchVendors();
    } catch (err: any) { enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' }); }
    finally { setDialogBusy(false); }
  };

  const handleEditParameter = async () => {
    if (!editParam) return;
    setDialogBusy(true);
    try {
      await api.put(`/vendor-suggestions/master/parameter/${editParam._id}`, {
        standardizedName: editParam.standardizedName,
        aliases: editParam.aliases,
        category: editParam.category || null
      });
      enqueueSnackbar('Updated', { variant: 'success' });
      setEditOpen(false); setEditParam(null); await fetchMaster();
    } catch (err: any) { enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' }); }
    finally { setDialogBusy(false); }
  };

  const handleDeleteParameter = async (id: string) => {
    try {
      await api.delete(`/vendor-suggestions/master/parameter/${id}`);
      enqueueSnackbar('Deleted', { variant: 'success' });
      await fetchMaster(); await fetchVendors();
    } catch (err: any) { enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' }); }
  };

  // ===== CSV Import =====
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setCsvData(text);
      parseCsvPreview(text);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Mirrors the backend bulkImportCSV header sniff so the preview shows what
  // will actually land in the DB regardless of which of the two accepted
  // formats the user pasted.
  const parseCsvPreview = (text: string) => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) { setCsvPreview([]); return; }

    const headerNorm = lines[0].toLowerCase().replace(/\s+/g, ' ').trim();
    const isLabFormat = headerNorm === 'lab name,group name,configured parameter names';

    const preview = lines.slice(1, 11).map(line => {
      const fields = parseRFC4180Line(line);
      if (isLabFormat) {
        const namesCell = (fields[2] || '').trim();
        // Multi-name cells contain inner quotes ("a", "b"); bare cells don't.
        // A bare cell like `BILIRUBIN, DIRECT` is one name, comma included.
        const names = namesCell.indexOf('"') !== -1
          ? parseRFC4180Line(namesCell).map(n => n.trim()).filter(Boolean)
          : (namesCell ? [namesCell] : []);
        const standardizedName = names[0] || '';
        const stdLower = standardizedName.toLowerCase();
        const aliases = names.slice(1).filter(n => n.toLowerCase() !== stdLower);
        return {
          standardizedName,
          aliases: aliases.join(', '),
          category: (fields[1] || '').trim()
        };
      }
      return {
        standardizedName: (fields[0] || '').trim(),
        aliases: (fields[1] || '').trim(),
        category: (fields[2] || '').trim()
      };
    });
    setCsvPreview(preview);
  };

  // Proper RFC-4180 single-line parser. Handles "" as escaped quote inside
  // a quoted field and preserves commas that appear inside "..." — needed
  // for lab-export rows like "GLUCOSE FASTING (FBS), FLUORIDE PLASMA".
  const parseRFC4180Line = (line: string): string[] => {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    while (i < line.length) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i += 2;
            continue;
          }
          inQuotes = false;
          i++;
          continue;
        }
        current += ch;
        i++;
      } else {
        if (ch === '"') { inQuotes = true; i++; }
        else if (ch === ',') { fields.push(current); current = ''; i++; }
        else { current += ch; i++; }
      }
    }
    fields.push(current);
    return fields;
  };

  const handleCsvImport = async () => {
    if (!selectedVendor) { enqueueSnackbar('Select a vendor first', { variant: 'warning' }); return; }
    if (!csvData) { enqueueSnackbar('No CSV data', { variant: 'warning' }); return; }
    setImporting(true);
    try {
      const res = await api.post('/vendor-suggestions/master/bulk-import', {
        vendorName: selectedVendor,
        vendorAliases: csvVendorAliases.split(',').map(a => a.trim()).filter(Boolean),
        csvData
      });
      enqueueSnackbar(`Imported ${res.data.imported} parameters (${res.data.errors?.length || 0} errors). Total: ${res.data.total}`, { variant: 'success' });
      setCsvOpen(false); setCsvData(''); setCsvPreview([]); setCsvVendorAliases('');
      await fetchMaster(); await fetchVendors();
    } catch (err: any) { enqueueSnackbar(err.response?.data?.message || 'Import failed', { variant: 'error' }); }
    finally { setImporting(false); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Vendor Master</Typography>
          {!canEditMasters && <Chip label="View only" size="small" sx={{ ml: 1.5 }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Vendor</InputLabel>
            <Select value={selectedVendor} label="Vendor" onChange={e => { setSelectedVendor(e.target.value); setPage(1); setMasterPage(1); }}>
              <MenuItem value="">All Vendors</MenuItem>
              {vendors.map(v => (
                <MenuItem key={v.vendorName} value={v.vendorName}>
                  {v.vendorName} ({v.parameterCount})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {canEditMasters && (
            <>
              <Tooltip title="Add New Vendor"><IconButton color="primary" onClick={() => setAddVendorOpen(true)}><AddIcon /></IconButton></Tooltip>
              <Tooltip title={selectedVendor ? `Delete vendor "${selectedVendor}"` : 'Select a vendor to delete'}>
                <span>
                  <IconButton color="error" disabled={!selectedVendor} onClick={() => setDeleteVendorOpen(true)}>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="Refresh"><IconButton onClick={() => { tab === 0 ? fetchSuggestions() : fetchMaster(); fetchVendors(); }}><RefreshIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {canEditMasters && <Tab value={0} label="Suggestions" />}
          <Tab value={1} label="Vendor Master" />
        </Tabs>
      </Paper>

      {/* ==================== SUGGESTIONS TAB ==================== */}
      {tab === 0 && canEditMasters && (
        <Paper sx={{ p: 2 }}>
          {selected.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              <Typography variant="body2">{selected.size} selected</Typography>
              <Button variant="contained" color="success" size="small" startIcon={bulkBusy ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />} disabled={bulkBusy} onClick={handleBulkApprove}>Approve Selected</Button>
              <Button variant="outlined" color="error" size="small" startIcon={<RejectIcon />} disabled={bulkBusy} onClick={() => openRejectDialog(Array.from(selected))}>Reject Selected</Button>
            </Box>
          )}
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : suggestions.length === 0 ? <Alert severity="info">No pending suggestions.</Alert> : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox"><Checkbox checked={selected.size === suggestions.length && suggestions.length > 0} indeterminate={selected.size > 0 && selected.size < suggestions.length} onChange={toggleSelectAll} /></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Standardized Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Proposed Alias</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Freq</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Suggested By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestions.map(s => (
                      <TableRow key={s._id} hover selected={selected.has(s._id)}>
                        <TableCell padding="checkbox"><Checkbox checked={selected.has(s._id)} onChange={() => toggleSelected(s._id)} /></TableCell>
                        <TableCell>{s.vendorName}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{s.standardizedName}</TableCell>
                        <TableCell><strong>{s.proposedAlias}</strong></TableCell>
                        <TableCell><Chip label={sourceLabel(s.source)} size="small" /></TableCell>
                        <TableCell align="center"><Chip label={s.frequency} size="small" color={s.frequency >= 3 ? 'primary' : 'default'} /></TableCell>
                        <TableCell>{s.suggestedBy.slice(0, 3).map(u => u.name || u.email || '?').join(', ')}{s.suggestedBy.length > 3 && ` +${s.suggestedBy.length - 3}`}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Approve"><span><IconButton size="small" color="success" disabled={actingId === s._id} onClick={() => handleApprove(s._id)}>{actingId === s._id ? <CircularProgress size={18} /> : <ApproveIcon />}</IconButton></span></Tooltip>
                          <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => openRejectDialog([s._id])}><RejectIcon /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {pages > 1 && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><Pagination count={pages} page={page} onChange={(_, v) => setPage(v)} /></Box>}
            </>
          )}
        </Paper>
      )}

      {/* ==================== VENDOR MASTER TAB ==================== */}
      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          {canEditMasters && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button variant="contained" startIcon={<AddIcon />} disabled={!selectedVendor} onClick={() => { setParamForm({ standardizedName: '', aliases: '', category: '' }); setAddParamOpen(true); }}>
                Add Parameter
              </Button>
              <Button variant="outlined" startIcon={<UploadIcon />} disabled={!selectedVendor} onClick={() => { setCsvData(''); setCsvPreview([]); setCsvVendorAliases(''); setCsvOpen(true); }}>
                Import CSV
              </Button>
              {!selectedVendor && <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>Select a vendor to add parameters or import CSV</Typography>}
            </Box>
          )}

          {masterLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : master.length === 0 ? <Alert severity="info">No entries found.{selectedVendor ? (canEditMasters ? ' Add parameters or import a CSV.' : '') : ' Select a vendor.'}</Alert> : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Standardized Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Aliases</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reference&nbsp;(unit&nbsp;·&nbsp;method&nbsp;·&nbsp;low–high)</TableCell>
                      {canEditMasters && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {master.map(m => (
                      <TableRow key={m._id} hover>
                        <TableCell>{m.vendorName}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{m.standardizedName}</TableCell>
                        <TableCell><Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{(m.aliases || []).map((a, i) => <Chip key={i} label={a} size="small" variant="outlined" />)}</Box></TableCell>
                        <TableCell>{m.category || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {m.reference && (m.reference.female || m.reference.male || m.reference.neutral)
                            ? (['female', 'male', 'neutral'] as const).map((g) => {
                                const r = m.reference?.[g]; if (!r) return null;
                                const rng = [r.low, r.high].filter((v) => v != null && String(v) !== '').join(' – ') || (r.referenceRange || '—');
                                return (
                                  <div key={g}>
                                    <b>{g[0].toUpperCase()}:</b> {r.unit || '—'} · {r.method || '—'} · {rng}
                                  </div>
                                );
                              })
                            : <span style={{ color: '#c0392b' }}>— no reference loaded —</span>}
                        </TableCell>
                        {canEditMasters && (
                          <TableCell align="right">
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditParam({ ...m }); setEditOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteParameter(m._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {masterPages > 1 && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><Pagination count={masterPages} page={masterPage} onChange={(_, v) => setMasterPage(v)} /></Box>}
            </>
          )}
        </Paper>
      )}

      {/* ===== Add Vendor Dialog ===== */}
      <Dialog open={addVendorOpen} onClose={() => setAddVendorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Vendor</DialogTitle>
        <DialogContent>
          <TextField label="Vendor Name" fullWidth required sx={{ mt: 1, mb: 2 }} value={newVendorName} onChange={e => setNewVendorName(e.target.value)} placeholder="e.g., Thyrocare" />
          <TextField label="Lab Name Aliases (comma-separated)" fullWidth multiline rows={2} value={newVendorAliases} onChange={e => setNewVendorAliases(e.target.value)} placeholder="e.g., Thyrocare Labs, THYROCARE TECHNOLOGIES PVT LTD" helperText="Variations of the lab name that Gemini might extract from PDFs" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVendorOpen(false)} disabled={dialogBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateVendor} disabled={dialogBusy || !newVendorName.trim()}>{dialogBusy ? <CircularProgress size={18} /> : 'Create Vendor'}</Button>
        </DialogActions>
      </Dialog>

      {/* ===== Delete Vendor Confirm Dialog ===== */}
      <Dialog open={deleteVendorOpen} onClose={() => !deleteBusy && setDeleteVendorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Vendor</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete vendor <strong>{selectedVendor}</strong> and all of its parameter master rows
            ({vendors.find(v => v.vendorName === selectedVendor)?.parameterCount ?? 0} parameter
            {(vendors.find(v => v.vendorName === selectedVendor)?.parameterCount ?? 0) === 1 ? '' : 's'}).
            Pending suggestions queued for this vendor will also be removed. Historical reports already
            published against this vendor are not affected.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This cannot be undone. Re-creating the vendor will require importing the master CSV again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteVendorOpen(false)} disabled={deleteBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteVendor} disabled={deleteBusy}>
            {deleteBusy ? <CircularProgress size={18} color="inherit" /> : 'Delete Vendor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Add Parameter Dialog ===== */}
      <Dialog open={addParamOpen} onClose={() => setAddParamOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Parameter to {selectedVendor}</DialogTitle>
        <DialogContent>
          <TextField label="Standardized Name" fullWidth required sx={{ mt: 1, mb: 2 }} value={paramForm.standardizedName} onChange={e => setParamForm({ ...paramForm, standardizedName: e.target.value })} placeholder="e.g., Hemoglobin" />
          <TextField label="Aliases (comma-separated)" fullWidth multiline rows={2} sx={{ mb: 2 }} value={paramForm.aliases} onChange={e => setParamForm({ ...paramForm, aliases: e.target.value })} placeholder="e.g., Hb, HGB, Haemoglobin" />
          <TextField label="Category" fullWidth value={paramForm.category} onChange={e => setParamForm({ ...paramForm, category: e.target.value })} placeholder="e.g., Hematology" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddParamOpen(false)} disabled={dialogBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateParameter} disabled={dialogBusy || !paramForm.standardizedName.trim()}>{dialogBusy ? <CircularProgress size={18} /> : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* ===== Edit Parameter Dialog ===== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Parameter</DialogTitle>
        <DialogContent>
          {editParam && (
            <>
              <TextField label="Standardized Name" fullWidth sx={{ mt: 1, mb: 2 }} value={editParam.standardizedName} onChange={e => setEditParam({ ...editParam, standardizedName: e.target.value })} />
              <TextField label="Aliases (comma-separated)" fullWidth multiline rows={2} sx={{ mb: 2 }} value={(editParam.aliases || []).join(', ')} onChange={e => setEditParam({ ...editParam, aliases: e.target.value.split(',').map(a => a.trim()).filter(Boolean) })} />
              <TextField label="Category" fullWidth value={editParam.category || ''} onChange={e => setEditParam({ ...editParam, category: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={dialogBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleEditParameter} disabled={dialogBusy}>{dialogBusy ? <CircularProgress size={18} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ===== CSV Import Dialog ===== */}
      <Dialog open={csvOpen} onClose={() => setCsvOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import CSV — {selectedVendor}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Two CSV formats are accepted (auto-detected from the header row):
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Format 1 — manual:</strong><br />
            <code>standardizedName,aliases,category</code><br />
            Hemoglobin,&quot;Hb,HGB,Haemoglobin&quot;,Hematology<br />
            Total WBC Count,&quot;WBC,TLC&quot;,Hematology
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Format 2 — lab export:</strong><br />
            <code>Lab Name,Group Name,Configured Parameter Names</code><br />
            Column 3 packs every name in one cell as quoted, comma-separated entries — the first is the standardized name, the rest become aliases.
            Column 1 must match the vendor selected above (we'll reject the file otherwise).
          </Alert>
          <TextField label="Vendor Lab-Name Aliases (comma-separated, optional)" fullWidth sx={{ mb: 2 }} value={csvVendorAliases} onChange={e => setCsvVendorAliases(e.target.value)} placeholder="e.g., Thyrocare Labs, THYROCARE TECHNOLOGIES" helperText="Optional — will be set on all imported entries" />
          <input type="file" accept=".csv,.txt" ref={fileRef} style={{ display: 'none' }} onChange={handleFileSelect} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()}>Select CSV File</Button>
            <Typography variant="body2" color="text.secondary">or paste CSV below</Typography>
          </Box>
          <TextField label="CSV Data" fullWidth multiline rows={6} value={csvData} onChange={e => { setCsvData(e.target.value); parseCsvPreview(e.target.value); }} placeholder="standardizedName,aliases,category&#10;Hemoglobin,&quot;Hb,HGB&quot;,Hematology" />
          {csvPreview.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Preview (first {csvPreview.length} rows):</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Standardized Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Aliases</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvPreview.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.standardizedName}</TableCell>
                        <TableCell>{row.aliases}</TableCell>
                        <TableCell>{row.category || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvOpen(false)} disabled={importing}>Cancel</Button>
          <Button variant="contained" onClick={handleCsvImport} disabled={importing || !csvData.trim()} startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}>
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Reject Dialog ===== */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, ids: [], reason: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject {rejectDialog.ids.length === 1 ? 'Suggestion' : `${rejectDialog.ids.length} Suggestions`}</DialogTitle>
        <DialogContent>
          <TextField label="Reason for rejection" multiline rows={3} fullWidth required sx={{ mt: 1 }} value={rejectDialog.reason} onChange={e => setRejectDialog({ ...rejectDialog, reason: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, ids: [], reason: '' })} disabled={bulkBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitReject} disabled={bulkBusy || !rejectDialog.reason.trim()}>{bulkBusy ? <CircularProgress size={18} /> : 'Reject'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorMasterManagement;
